import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { HARBORWELL_LOGO } from "@/lib/api";
import { Anchor, Loader2, QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import MfaPanel from "@/components/MfaPanel";
import axios from "axios";

// Resolve the app's canonical URL at runtime rather than hard-coding a
// specific production domain — this keeps QR codes and share links
// pointing at the environment the user actually loaded (preview / prod /
// custom domain) instead of always sending them to production.
const APP_URL =
  (typeof window !== "undefined" && window.location.origin) ||
  process.env.REACT_APP_BACKEND_URL ||
  "";
const API = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const { user, login, completeMfa, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState(null); // { action, temp_token }
  const [googleSso, setGoogleSso] = useState({ configured: false, allowed_domains: [] });

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/api/sso/internal/google/status`)
      .then((r) => {
        if (!cancelled) setGoogleSso(r.data || { configured: false, allowed_domains: [] });
      })
      .catch(() => {
        /* Google SSO probe is best-effort; button stays hidden on failure. */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loading && user) {
    const target = user?.landing_route?.route || "/";
    return <Navigate to={target} replace />;
  }

  const expired = typeof window !== "undefined" && window.location.search.includes("expired=1");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    if (res.mfa_required) {
      setMfaChallenge({ action: res.mfa_action, temp_token: res.temp_token });
      return;
    }
    navigate(res.landing_route?.route || "/", { replace: true });
  };

  const handleMfaComplete = (data) => {
    const lr = completeMfa(data);
    navigate(lr?.route || "/", { replace: true });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F9F8F6]">
      {/* Brand Panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#1E3A8A] text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage:
              "url('https://customer-assets.emergentagent.com/job_anchor-care-1/artifacts/2csvr7c6_89C5E78B-311D-4AB1-A56D-AD4818FE07C2.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1E3A8A]/65 via-[#1E3A8A]/25 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A8A]/70 via-transparent to-transparent" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/95 p-1.5 flex items-center justify-center">
              <img src={HARBORWELL_LOGO} alt="HarborWell" className="h-full w-auto" />
            </div>
            <div>
              <div className="font-display text-xl font-bold">HarborWell</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#E8ECE7]">Connect</div>
            </div>
          </div>
        </div>
        <div className="relative max-w-md">
          <div className="hw-eyebrow text-[#F59E0B] mb-3">A guiding light</div>
          <h1 className="font-display text-4xl font-semibold leading-tight mb-4 text-white">
            Community wellness, one Care Seeker at a time.
          </h1>
          <p className="text-white/85 leading-relaxed mb-6">
            HarborWell Connect helps our team coordinate support, navigate resources, and
            walk alongside families across Eastern North Carolina — before challenges
            become crises.
          </p>
          <ul className="space-y-2.5 text-sm text-white/90">
            {[
              "A.N.C.H.O.R. intake in under 2 minutes",
              "One-tap follow-ups & wellness notes from the field",
              "Funder-ready monthly impact reports",
            ].map((line) => (
              <li key={line} className="flex items-start gap-2.5">
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#F59E0B] flex-shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
          <a
            href="/impact"
            className="inline-flex items-center gap-1.5 mt-6 text-xs font-medium text-white/90 hover:text-white border-b border-white/40 hover:border-white/80 transition-colors pb-0.5"
            data-testid="login-impact-link"
          >
            See HarborWell's impact gallery →
          </a>
        </div>
        <div className="relative text-xs text-white/70 flex items-center gap-2">
          <Anchor className="w-3.5 h-3.5" strokeWidth={1.75} />
          A 501(c)(3) nonprofit · Eastern North Carolina
        </div>
      </div>

      {/* Form Panel */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="flex lg:hidden items-center justify-center mb-8">
            <img src={HARBORWELL_LOGO} alt="HarborWell" className="h-16 w-auto" />
          </div>
          <div className="hw-eyebrow mb-2">Welcome back</div>
          <h2 className="font-display text-3xl font-semibold text-[#0F172A] mb-2">Sign in to Connect</h2>
          <p className="text-[#334155] mb-8">Continue caring for your community.</p>

          {mfaChallenge ? (
            <MfaPanel
              challenge={mfaChallenge}
              onComplete={handleMfaComplete}
              onCancel={() => { setMfaChallenge(null); setPassword(""); }}
            />
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            {expired && (
              <div className="px-4 py-3 rounded-xl bg-[#FEF3C7] border border-[#FCD34D] text-sm text-[#92400E]">
                Your session ended. Please sign in again.
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D] focus:border-transparent text-[#0F172A]"
                placeholder="you@harborwell.care"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D] focus:border-transparent text-[#0F172A]"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-btn"
              className="w-full py-3 rounded-full bg-[#1E3A8A] text-white font-medium hover:bg-[#172554] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? "Signing in…" : "Sign in"}
            </button>

            {googleSso.configured && (
              <>
                <div className="flex items-center gap-3 my-1" data-testid="login-google-divider">
                  <div className="flex-1 h-px bg-[#E5E5E0]" />
                  <span className="text-xs uppercase tracking-widest text-[#7E937D]">or</span>
                  <div className="flex-1 h-px bg-[#E5E5E0]" />
                </div>
                <a
                  href={`${API}/api/sso/internal/google/login`}
                  data-testid="login-google-btn"
                  className="w-full py-3 rounded-full border border-[#E5E5E0] bg-white text-[#1E3A8A] font-medium hover:bg-[#F9F8F6] transition-colors flex items-center justify-center gap-2"
                >
                  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
                    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 18.9 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
                    <path fill="#4CAF50" d="M24 44c5.5 0 10.4-2.1 14.2-5.5l-6.6-5.4c-2 1.4-4.7 2.4-7.6 2.4-5.2 0-9.6-3.4-11.2-8L6.2 32c3.3 6.7 10.1 11.3 17.8 11.3z" />
                    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.4l.1.1 6.6 5.4c-.5.4 6.7-4.8 6.7-14.9 0-1.3-.1-2.4-.4-3.5z" />
                  </svg>
                  Continue with Google
                </a>
                {googleSso.allowed_domains?.length > 0 && (
                  <p className="text-[10px] text-[#7E937D] text-center" data-testid="login-google-domains-hint">
                    For {googleSso.allowed_domains.join(", ")} accounts.
                  </p>
                )}
              </>
            )}

            <p className="text-xs text-[#1E3A8A] text-center mt-6">
              Trouble signing in? Contact your administrator.
            </p>
          </form>
          )}

          {/* Mobile-friendly QR: scan to open Connect on a phone */}
          {!mfaChallenge && (
            <div
              data-testid="login-qr-panel"
              className="mt-8 pt-6 border-t border-[#E5E5E0] flex items-center gap-4"
            >
              <div className="bg-white p-2 rounded-xl border border-[#E5E5E0]">
                <QRCodeSVG
                  data-testid="login-qr-code"
                  value={APP_URL}
                  size={88}
                  level="M"
                  bgColor="#FFFFFF"
                  fgColor="#1E3A8A"
                  includeMargin={false}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 text-[#1E3A8A] mb-1">
                  <QrCode className="w-3.5 h-3.5" strokeWidth={1.75} />
                  <span className="hw-eyebrow">Open on your phone</span>
                </div>
                <p className="text-xs text-[#334155] leading-relaxed">
                  Scan to open Connect on a mobile device. Sign in with the
                  same credentials — perfect for field visits.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
